<template>
  <BaseLayout>
    <BaseDashboard
      :is-loading="isLoading"
      id="EditCampaign"
    >
      <div
        class="page-header bg-secondary m-0 p-3 border-bottom"
      >
        <span class="page-title mr-4">Editing - {{ campaign.name }}</span>
        <span :class="['label px-2', getClassForLabel]">{{ campaign.state }}</span>
        <div class="page-options d-flex">
          <b-btn
            v-if="tabIndex===0"
            variant="primary"
            class="ml-2 border-0"
            data-test="save-changes-button"
            @click="saveChanges"
            :disabled="isSaveButtonDisabled"
            :class="{ 'btn-loading': isButtonLoading }"
          >
            Save changes
          </b-btn>
          <feature
            name="SHOWCASE_CREATIVE"
          >
            <router-link
              v-if="tabIndex === 2"
              class="share-btn btn btn-secondary btn-sm mr-3"
              :to="{
                name: 'Showcase',
                params: {
                  creativeKey: creativeKey,
                }
              }"
              target="_blank"
              data-test="showcase-share-button"
              v-b-tooltip.hover
              title="Share a showcase of your creative and content with third parties"
              @click.native.capture="handleClick"
              :disabled="isCampaignReadonly"
            >
              Share preview
              <i class="fal fa-share-square ml-2" />
            </router-link>
          </feature>
        </div>
      </div>
      <b-tabs

        id="EditCampaign"
        class="overflow-hidden campaign-edit-nav"
        nav-wrapper-class="bg-white border-bottom panel-nav"
        nav-class="px-3"
        v-model="tabIndex"
        lazy
      >
        <b-tab
          class="overflow-auto"
        >
          <template v-slot:title>
            <i class="fa-light fa-pen-alt" /> Basic Info
          </template>
          <b-row
            class="m-0"
          >
            <b-col
              class="p-5"
              cols="auto"
              lg="8"
            >
              <CampaignInfo
                @updateOrderDetails="updateOrderDetails"
                @setNotValidForNavigation="updateIsNotValidForNavigation"
                @next-step="nextStep"
                @setIsDirty="setIsDirty"
                :key="campaignInfoKey"
                :read-only="readOnly"
                :iscurrency-set="true"
                ref="campaign-info"
              />
            </b-col>
            <b-col
              cols="4"
              class="p-5"
            >
              <CampaignDetailsCard
                ref="CampaignDetailsCard"
              />
            </b-col>
          </b-row>
        </b-tab>
        <b-tab
          class="h-100 overflow-auto"
          title-link-class="edit-campaign-content-tab"
        >
          <template v-slot:title>
            <i class="fal fa-list-alt" /> Content
          </template>
          <new-content-info
            ref="new-content-info"
            @setIsValidForNavigation="setIsValidForNavigation"
          />
        </b-tab>
        <b-tab
          class="h-100"
          title-link-class="edit-campaign-creative-tab"
        >
          <template v-slot:title>
            <i class="fal fa-paintbrush-pencil" /> Creative
          </template>
          <ContentEditor
            :read-only="readOnly"
            view-type="Editor"
            @setNotValidForNavigation="updateIsNotValidForNavigation"
          />
        </b-tab>
        <b-tab
          class="overflow-auto"
        >
          <template v-slot:title>
            <i class="fa-light fa-coins" /> Billing
          </template>
          <CampaignInvoicing
            :edit-mode="true"
            :is-live="isCampaignLive"
          />
        </b-tab>
      </b-tabs>
    </BaseDashboard>
    <ShareModal
      :campaign-name="campaign.title"
      :campaign="campaign"
    />
    <campaign-changes-modal
      :is-apply-changes-button-loading="isApplyChangesButtonLoading"
      @applyChanges="applyChanges()"
      @discardChanges="discardChanges()"
      @continueEditing="isButtonLoading = false"
    />
  </BaseLayout>
</template>

<script>
import {
  mapActions, mapGetters, mapState, mapMutations,
} from 'vuex';
import * as types from '@/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';
import CampaignInfo from '@/modules/campaigns/components/CampaignInfo';
import NewContentInfo from '@/modules/content/components/ContentPage/ContentInfo';
import ContentEditor from '@/modules/content/components/ContentEditor';
import CampaignInvoicing from '@/modules/campaigns/components/CampaignInvoicing';
import CampaignDetailsCard from '@/modules/campaigns/components/CampaignDetailsCard';
import ShareModal from '@/modules/share/components/ShareModal';

import CampaignChangesModal from '@/modules/campaigns/components/CampaignChangesModal';

import campaignMixin from '@/core/mixins/campaignHelper';

export default {
  name: 'EditCampaign',
  mixins: [
    campaignMixin,
  ],
  components: {
    CampaignInfo,
    NewContentInfo,
    ContentEditor,
    CampaignDetailsCard,
    ShareModal,
    CampaignInvoicing,
    CampaignChangesModal,
  },
  data() {
    return {
      isLoading: true,
      isButtonLoading: false,
      isApplyChangesButtonLoading: false,
      campaignInfoKey: 0,
      tabIndex: 0,
      isValidLastButton: false,
      isValidNextButton: false,
      isDirty: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaignFeedId',
      'campaign',
      'campaignChanges',
      'order',
      'orderChanges',
    ]),
    ...mapGetters('campaigns', {
      isCampaignState: campaignTypes.GET_IS_CAMPAIGN_STATE,
      isCampaignReadonly: campaignTypes.GET_IS_CAMPAIGN_READONLY,
      readOnly: campaignTypes.GET_READ_ONLY_FEATURES,
      GET_CAMPAIGN_TOTAL_BUDGET: campaignTypes.GET_CAMPAIGN_TOTAL_BUDGET,
    }),
    ...mapState('content', [
      'activeCreatives',
      'creative',
    ]),
    isSaveButtonDisabled() {
      return this.isCampaignReadonly
      || !this.isValidLastButton
      || !this.isValidNextButton
      || !this.isDirty;
    },
    isCampaignLive() {
      return this.campaign.state === 'LIVE';
    },
    creativeKey() {
      return this.creative.paused ? this.activeCreatives[0].creativeKey : this.creative.creativeKey;
    },
    wasBudgetChanged() {
      return this.order.budget !== this.GET_CAMPAIGN_TOTAL_BUDGET;
    },
    getClassForLabel() {
      return this.getLabelClass();
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_EXISTING_CONTENT_PROVIDER,
      campaignTypes.FETCH_EXISTING_ADVERTISERS,
      campaignTypes.CREATE_ADVERTISER,
      campaignTypes.FETCH_CURRENCIES,
      campaignTypes.FETCH_BANNED_WORDS,
      campaignTypes.FETCH_EXISTING_CAMPAIGN,
      campaignTypes.FETCH_ORDER,
      campaignTypes.FETCH_EXISTING_BLACKLIST,
      campaignTypes.RESET_CAMPAIGN,
      campaignTypes.UPDATE_SELECTED_ADVERTISER,
      campaignTypes.SET_VIEW_TYPE,
      campaignTypes.SET_CAMPAIGN_STATE,
      campaignTypes.APPEND_TEMP_READ_ONLY_FEATURES,
      campaignTypes.SET_CAMPAIGN_INFO_CHANGES,
      campaignTypes.PATCH_ORDER,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_EXISTING_CONTENT_SCRAPES,
      contentTypes.FETCH_FEED_CONTENT_ITEMS,
      contentTypes.FETCH_EXISTING_CREATIVES,
      contentTypes.FETCH_FILTERED_FEED_CONTENT_ITEMS,
      contentTypes.FETCH_CREATIVE_TEMPLATES,
    ]),
    ...mapMutations('content', [
      contentTypes.SET_CONTENT_LIST_TYPE,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    handleClick(event) {
      if (this.isCampaignReadonly) {
        event.preventDefault();
      }
    },
    fetchCampaignPageEntities(campaignId) {
      const contentProviderPromise = this.FETCH_EXISTING_CONTENT_PROVIDER();
      const currenciesPromise = this.FETCH_CURRENCIES();
      const bannedWordsPromise = this.FETCH_BANNED_WORDS();
      Promise.all([contentProviderPromise, currenciesPromise, bannedWordsPromise])
        .then(() => {
          const { advertiserId } = this.$route.params;
          if (typeof campaignId !== 'undefined') {
            this.fetchExistingCampaignEntities(advertiserId, campaignId);
          } else { this.isLoading = false; }
        });
    },
    fetchExistingCampaignEntities(advertiserId, campaignId) {
      const campaignPromise = this.FETCH_EXISTING_CAMPAIGN({ advertiserId, campaignId });
      const orderPromise = this.FETCH_ORDER({ advertiserId, campaignId, setOrder: true });
      Promise.all([
        campaignPromise,
        orderPromise,
      ])
        .then(async (res) => {
          await this.FETCH_EXISTING_CREATIVES({ advertiserId });
          await this.FETCH_EXISTING_BLACKLIST(res[0].blacklistRuleSetId);
          if (this.$route.params.hasOwnProperty('targetPage')) this.tabIndex = this.$route.params.targetPage;
          this.isLoading = false;
        });
    },
    updateOrderDetails(payload) {
      if ((this.$refs.CampaignDetailsCard)) {
        this.$refs.CampaignDetailsCard.setOrderDetails(payload);
      }
    },
    async handleCreate() {
      this.isLoading = true;
      const { campaignId, advertiserId } = this.$route.params;
      this.campaignId = campaignId;
      this.RESET_CAMPAIGN();
      const advertisers = await this.FETCH_EXISTING_ADVERTISERS();
      const aid = Number.parseInt(advertiserId, 10) || advertisers[0].id;
      const advertiserSet = advertisers
        .find((advertiser) => advertiser.id === aid);
      if (advertiserId && advertiserSet) {
        await this.UPDATE_SELECTED_ADVERTISER(advertiserSet);
      }
      this.fetchCampaignPageEntities(campaignId);
    },
    displayGlobalMessage() {
      if (this.wasBudgetChanged) {
        this.SET_GLOBAL_MESSAGE({
          message: 'Your changes have been sent for APPROVAL. You will receive an email when your Campaign is LIVE',
          type: 'info',
        });
        return;
      }
      this.SET_GLOBAL_MESSAGE({
        message: 'Campaign has been updated',
        type: 'info',
      });
    },
    nextStep() {
      this.isDirty = false;
    },
    updateIsNotValidForNavigation(type, valid) {
      this.setIsValidForNavigation(type, !valid);
    },
    setIsValidForNavigation(type, isValid) {
      if (type === 'last') {
        this.isValidLastButton = isValid;
      } else if (type === 'next') {
        this.isValidNextButton = isValid;
      }
    },
    setIsDirty() {
      this.isDirty = true;
    },
    saveChanges() {
      this.isButtonLoading = true;
      if (this.isCampaignState('LIVE') && this.wasBudgetChanged) return this.$root.$emit('bv::show::modal', 'CampaignChangesModal');
      this.applyChanges();
    },
    async applyChanges() {
      this.isApplyChangesButtonLoading = true;
      if (this.isCampaignState('LIVE') && this.wasBudgetChanged) {
        this.APPEND_TEMP_READ_ONLY_FEATURES('budget');
        this.APPEND_TEMP_READ_ONLY_FEATURES('end-date');
        await this.SET_CAMPAIGN_STATE({
          advertiserId: this.campaign.advertiserId,
          campaignId: this.campaign.id,
          state: 'PAUSED',
        });
      }
      if (this.$refs['campaign-info']) {
        this.$refs['campaign-info'].onSubmit(true)
          .then(() => {
            this.displayGlobalMessage();
            if (this.wasBudgetChanged) {
              this.FETCH_EXISTING_CAMPAIGN({
                advertiserId: this.campaign.advertiserId,
                campaignId: this.campaign.id,
              });
            }
            this.SET_CAMPAIGN_INFO_CHANGES({});
            this.isApplyChangesButtonLoading = false;
            this.isDirty = false;
          })
          .finally(() => {
            this.isButtonLoading = false;
          });
      }

      this.isButtonLoading = false;
    },
    discardChanges() {
      this.$refs['campaign-info'].resetPeriods();
      this.isButtonLoading = false;
    },
  },
  created() {
    this.SET_CONTENT_LIST_TYPE('EDITOR');
    this.SET_VIEW_TYPE('Editor');
    this.handleCreate();
  },
  mounted() {
    window.onbeforeunload = () => (this.isDirty ? true : null);
  },
  beforeRouteLeave(to, from, next) {
    const h = this.$createElement;
    if (!this.isDirty) return next();
    this.$bvModal.msgBoxConfirm(h('p', [
      'You have unsaved changes!',
      h('br'),
      'Press "Proceed" to continue without saving, or "Cancel" to go back and review your changes',
    ]), {
      title: 'Warning',
      size: 'sm',
      buttonSize: 'sm',
      okTitle: 'Proceed',
      okVariant: 'danger',
      cancelTitle: 'Cancel',
      cancelVariant: 'tertiary',
      footerClass: 'p-2',
      hideHeaderClose: false,
      centered: true,
    })
      .then((value) => {
        if (!value) return next(false);
        return next();
      })
      .catch(() => next(false));
  },
};
</script>
<style lang="scss">
 .campaign-edit-nav{
  //  background: #fbfbfc;
   height: calc(100% - 65px);
 }
.nav-tabs .nav-item i {
  margin: 0;
  width: 2rem;
}
.share-btn[disabled] {
  cursor: not-allowed !important;
  opacity: 0.65;
}
</style>
